import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import http from '../../../../../../shared/http'
import { TApi, TRoutes } from '../../../../../../shared/const'

import styles from './style.module.scss'

/*
 * Verweis von einer Einstellung auf die Vorlage, die sie auslöst.
 *
 * Eine Frist ist nur die halbe Auskunft: Wer 14 Tage einstellt, will wissen,
 * was nach 14 Tagen bei den Schüler:innen ankommt — und will den Text
 * gegebenenfalls ändern. Bis 05.08.2026 führte der Weg dorthin über das Menü,
 * den Bereich Kommunikation und das Suchen in einer Liste von über dreißig
 * Vorlagen.
 *
 * Gezeigt wird der Name aus dem Katalog, nicht der technische Schlüssel. Damit
 * er nicht neben dem Katalog ein zweites Mal im Code steht, wird er einmal je
 * Sitzung vom Server geholt; bis dahin steht der Schlüssel da. Bleibt die
 * Abfrage erfolglos, verschwindet der Verweis nicht — er führt dann eben mit
 * dem Schlüssel als Beschriftung ans Ziel.
 */

// Einmal je Sitzung, nicht einmal je Einstellung: Auf dem Reiter „Fristen &
// Erinnerungen" hängen fünf Karten an denselben beiden Listen.
let namesPromise = null

const loadTemplateNames = () => {
	if (namesPromise) return namesPromise

	namesPromise = Promise.all([
		http.get(TApi.ADMIN_COMM_EMAILS).catch(() => ({ data: [] })),
		http.get(TApi.ADMIN_COMM_INTERNAL).catch(() => ({ data: [] }))
	]).then(([emails, internal]) => {
		const collect = (rows, channel) =>
			(Array.isArray(rows?.data) ? rows.data : []).reduce((acc, row) => {
				if (row?.key)
					acc[`${channel}:${row.key}`] = {
						title: row.title || row.subject || row.key,
						isActive: row.isActive !== false
					}
				return acc
			}, {})

		return { ...collect(emails, 'email'), ...collect(internal, 'internal') }
	})

	return namesPromise
}

export default function TemplateLinks({ items, label }) {
	const { t } = useTranslation()
	const [names, setNames] = useState({})

	useEffect(() => {
		let alive = true
		loadTemplateNames().then(loaded => {
			if (alive) setNames(loaded)
		})
		return () => {
			alive = false
		}
	}, [])

	if (!items?.length) return null

	return (
		<div className={styles.root}>
			<span className={styles.label}>
				{label || (items.length > 1 ? t('Related templates') : t('Related template'))}:
			</span>
			{items.map(item => {
				const template = names[`${item.channel}:${item.key}`]

				return (
					<Link
						key={`${item.channel}:${item.key}`}
						className={styles.link}
						to={`${TRoutes.COMMUNICATION}?tab=${item.channel}&item=${item.key}`}
					>
						<span className={styles.channel}>{item.channel === 'email' ? t('Email') : t('Message')}</span>
						{template?.title || item.key}
						{/* Eine abgeschaltete Vorlage geht nicht hinaus — die Frist
						    daneben wirkt dann ins Leere. */}
						{template && !template.isActive && <span className={styles.off}>{t('switched off')}</span>}
					</Link>
				)
			})}
		</div>
	)
}

TemplateLinks.propTypes = {
	items: PropTypes.arrayOf(
		PropTypes.shape({
			channel: PropTypes.oneOf(['email', 'internal']).isRequired,
			key: PropTypes.string.isRequired
		})
	).isRequired,
	label: PropTypes.string
}
